<div class="header">
  <div class="content">
    <div class="contentInner">
      <div class="pageTitle" appReveal>
        <div class="headline">Dash Enterprise</div>
      </div>
    </div>
    <app-enterprise-tab-navigator></app-enterprise-tab-navigator>
  </div>
</div>

<div class="content">
  <div class="contentInner">
    <h1 class="sectionHeader">Using Dash with MDM</h1>
    <p class="intro">If you are using an MDM solution in your business, Dash Enterprise allows you to deploy and
      configure Dash using <i>managed configurations</i>.  Configure Dash's settings and also the behavior of your
      display device (for example by turning on kiosk mode to lock the device to Dash).
    </p>

    <p>We have created a standalone guide that shows you how to configure Dash using your MDM service:</p>

    <a class="button" href="/assets/Dash-Enterprise-MDM-configuration-guide.pdf" target="_blank">Download guide</a>

    <h2 class="sectionHeader">Setting up Dash in your MDM system</h2>
    <p>The following instructions explain how to configure Microsoft InTune to manage your Dash display devices.  It
      covers the process of adding Dash to InTune, and creating a configuration profile for your devices.  Other MDM
      systems are set up in a similar way, and if you would like help setting up Dash with your MDM system please
      <a routerLink="/contact">Contact us</a> for support.</p>

    <h3>Prerequisites</h3>
    <p>Before adding Dash to InTune, you should create one or more device groups for your display devices.  You should
      have one group corresponding to each unique configuration profile that you plan to make.  For example, if you
      plan to have a different configuration profile for each of your office locations (to allow for different
      branding etc.) then you should create a group for each location.  If you plan to use the same Dash options for
      all your display devices, you can just create a single group.
    </p>

    <p>For information on how to add groups in InTune, please see
      <a href="https://learn.microsoft.com/en-us/mem/intune/fundamentals/groups-add" target="_blank">Microsoft’s documentation</a>.
    </p>

    <p class="tip icon">
      <b>Note:</b> If you are planning to use InTune with Android devices, you will need to connect your InTune account to a
      Managed Google Play account before following the steps in our guide.  For information on how to do this,
      please see <a href="https://learn.microsoft.com/en-us/mem/intune/enrollment/connect-intune-android-enterprise" target="_blank">Microsoft’s documentation</a>.
    </p>

    <h3>iOS instructions</h3>
    <h4>Step 1: Add Dash to InTune</h4>

    <ol class="instructionList">
      <li>
        <div class="liContainer">
          <p>In your <a href="https://endpoint.microsoft.com/#home" target="_blank">Endpoint manager console</a>, click <i>Apps</i> then <i>All apps</i>.</p>
          <app-illustration class="illustration allAppsIllustration" alt="Select All Apps in the Endpoint manager console" src="../../assets/images/enterprise-intune-all-apps.png" webpSrc="../../assets/images/enterprise-intune-all-apps.webp" width="838" height="844"></app-illustration>
        </div>

      </li>
      <li>
        <div class="liContainer">
          <p>Click <i>Create</i> then select <i>iOS/iPadOS</i> as the platform and <i>iOS store app</i> as the type, then click <i>Select</i>.</p>
          <app-illustration class="illustration createAppIllustration" alt="Select iOS store app" src="../../assets/images/enterprise-create-intune-app.png" webpSrc="../../assets/images/enterprise-create-intune-app.webp" width="955" height="428"></app-illustration>
        </div>

      </li>
      <li>
        <div>
          <p>Click <i>Search</i> the app store and enter <i>“Dash meeting room display”</i>.  Dash should be returned in the search results.  Click it, then click <i>Select</i>.</p>
        </div>
      </li>
      <li>
        <div>
          <p>You should now see a page showing Dash’s app info.  The fields will be pre-filled for you, but it is important to change the <i>Minimum operating system</i> version to <i>“12.0”</i> as this is the minimum version of iOS that Dash supports.
          </p>
        </div>
      </li>
      <li>
        <div>
          <p>Assign the app to your desired groups.  Add groups to the <i>Required</i> section to have Dash installed automatically on devices in those groups.  Add Dash to the <i>Available for enrolled devices</i> section to make Dash available to install manually.
          </p>
          <app-illustration class="illustration assignGroupIllustration" alt="Assign Dash to device groups" src="../../assets/images/enterprise-intune-assign-group.png" webpSrc="../../assets/images/enterprise-intune-assign-group.webp" width="782" height="530"></app-illustration>
        </div>
      </li>
      <li>
        <div>
          <p>Click <i>Next</i> to load the review screen.  Make sure that all details are correct, then click <i>Create</i>.
          </p>
        </div>
      </li>
    </ol>


    <h4>Step 2: Create the managed profile for Dash</h4>
    <ol>
      <li><div><p>In the endpoint manager console, click <i>Apps</i> then <i>App configuration policies</i></p></div></li>
      <li><div><p>Enter a name for your configuration</p></div></li>
      <li><div><p>Click <i>Add</i> and select <i>Managed devices</i> as the type</p></div></li>
      <li><div><p>Set <i>iOS/iPadOS</i> as the <i>Platform</i> then click <i>Select app</i> (next to <i>Targeted app</i>) and choose <i>Dash</i> and click <i>Next</i></p></div></li>
      <li><div><p>Press <i>Next</i>.  You will now need to choose between using the configuration designer, or entering an XML configuration:</p></div></li>
    </ol>
    <h5>Configuration designer</h5>
    <p>The configuration designer allows you to enter each of Dash's settings, its type, and its value.  You can find
      documentation on each of Dash's options in our <a href="/assets/Dash-Enterprise-MDM-configuration-guide.pdf" target="_blank">MDM configuration guide PDF</a>.</p>

    <p>You will need to specify the license key, but all other configuration options are optional and any option not
      specified in your MDM configuration can be configured locally on the device.  We recommend configuring the
    <samp>calendarProvider</samp> option, as well as all of Dash's settings and branding in your MDM configuration, and
    setting the <samp>calendar</samp> option locally on the device.  This makes it easier to have a single MDM
    configuration cover multiple devices and meeting rooms.</p>

    <h5>iOS XML configuration</h5>
    <p>If you prefer, you can enter your configuration as a file.  iOS configuration files use the Property List (plist)
      file format (defined by Apple).  To make the process easier, Dash supports the AppConfig specification which
      allows you to generate a plist configuration file using a graphical interface.  Download our
      <a href="https://enterprise.get-dash.com/assets/dash-plist-config-spec.xml" target="_blank">Managed App Configuration specfile</a>
      and then use it with the <a href="https://appconfig.jamfresearch.com/generator" target="_blank">Jamf AppConfig Generator</a>.
      The generator will allow you to choose Dash's settings using a graphical interface, and will then generate a Plist
      file.  The generated file will include all possible Dash configuration options, but you can remove any options
      that you'd prefer to manage on the device from this file before you add it to your MDM system.
    </p>

    <p class="tip icon">Please note that we have observed a bug with Jamf's download functionality that can corrupt some
      of the values you enter in the GUI. We recommend copying and pasting the XML directly from the page rather than
      pressing the download button.</p>


    <h3>Android instructions</h3>
    <h4>Step 1: Add Dash to InTune</h4>
    <ol class="instructionList">
      <li>
        <div class="liContainer">
          <p>In your <a href="https://endpoint.microsoft.com/#home" target="_blank">Endpoint manager console</a>, click <i>Apps</i> then <i>All apps</i>.</p>
          <app-illustration class="illustration allAppsIllustration" alt="Select All Apps in the Endpoint manager console" src="../../assets/images/enterprise-intune-all-apps.png" webpSrc="../../assets/images/enterprise-intune-all-apps.webp" width="838" height="844"></app-illustration>
        </div>
      </li>
      <li>
        <div class="liContainer">
          <p>Click <i>Create</i> then select <i>Android</i> as the platform and <i>Store app</i> as the type, then choose <i>Managed Google Play app</i> and click <i>Select</i>.</p>
          <app-illustration class="illustration managedPlayIllustration" alt="Create a Managed Google Play app" src="../../assets/images/enterprise-intune-managed-play.png" webpSrc="../../assets/images/enterprise-intune-managed-play.webp" width="1459" height="927"></app-illustration>
        </div>
      </li>
      <li>
        <div>
          <p>Enter “Dash meeting room display” and click the <i>Search</i> button.  Dash should be returned in the search results.  Click it, then click <i>Approve</i>.  Approve any options you are asked for, then click <i>Select</i>, and click <i>Sync</i> (at the top of the window).
          </p>
        </div>
      </li>
      <li>
        <div>
          <p>You should be on the <i>All Apps screen</i>, and should see Dash in your list of apps as a <i>Managed Google Play store</i> app.  Note that it can take some time for Dash to appear in the list after it is approved.
          </p>
          <app-illustration class="illustration allAppsAndroidIllustration" alt="The All Apps screen in inTune, showing the iOS and Android versions of Dash" src="../../assets/images/enterprise-intune-all-apps-android.png" webpSrc="../../assets/images/enterprise-intune-all-apps-android.webp" width="1578" height="571"></app-illustration>
        </div>
      </li>
      <li>
        <div>
          <p>Assign the app to your desired groups by clicking on <i>Dash</i> in the apps list, then clicking <i>Properties</i> and clicking <i>Edit</i> (next to Assignments).  Add groups to the <i>Required</i> section to have Dash installed automatically on devices in those groups.  Add Dash to the <i>Available for enrolled devices</i> section to make Dash available to install manually.
          </p>
          <app-illustration class="illustration editAssignmentIllustration" alt="Assign Dash to device groups" src="../../assets/images/enterprise-intune-edit-assignments.png" webpSrc="../../assets/images/enterprise-intune-edit-assignments.webp" width="804" height="507"></app-illustration>
        </div>
      </li>
      <li>
        <div>
          <p>Click <i>Review+Save</i> to load the review screen.  Make sure that all details are correct, then click <i>Save</i>.
          </p>
        </div>
      </li>
    </ol>


    <h4>Step 2: Create the managed profile for Dash</h4>
    <ol>
      <li><div><p>In the <a href="https://endpoint.microsoft.com/#home" target="_blank">Endpoint manager console</a>, click <i>Apps</i> then <i>App configuration policies</i>.</p></div></li>
      <li><div><p>Click <i>Add</i> and select <i>Managed devices</i> as the type.</p></div></li>
      <li><div><p>Set <i>Android Enterprise</i> as the type</p></div></li>
      <li><div><p>Select the appropriate Profile type.  For Dash display devices, this is usually <i>Fully Managed, Dedicated,
        and Corporate-Owned Work Profile Only</i>, but if in doubt select <i>All Profile Types</i></p></div></li>
      <li><div><p>Click <i>Select app</i> (next to <i>Targeted app</i>) and choose <i>Dash</i> and click <i>Next</i></p></div></li>
      <li><div>
          <p>Under <i>Configuration settings format</i>, choose <i>Use configuration designer</i>.</p>

          <p>InTune will automatically detect Dash’s available configuration options.  To add an option to your Dash
            configuration, click the <i>Add</i> button and search for the option you would like to add.
            A full list of options is provided in our <a href="/assets/Dash-Enterprise-MDM-configuration-guide.pdf" target="_blank">MDM configuration guide PDF</a>.
          </p>

          <p>To finish adding an option, click the checkbox next to it, then click OK.  The option will be added to your
            configuration and set to its default value.  To change the value, click on the default value in the
            <i>Configuration value</i> column of the table, and enter a new value.  Choice type values can be chosen via a
            dropdown, and other values can be entered as text.  For detailed information on the allowed values for each
            option, please see our <a href="/assets/Dash-Enterprise-MDM-configuration-guide.pdf" target="_blank">MDM configuration guide PDF</a>.
          </p>
        </div>
      </li>
      <li>
        <p>Once you have finished setting your configuration options, click <i>Next</i> and then assign the profile to
          your desired groups (usually the same groups you assigned the Dash app to in Step 1).  Click <i>Next</i>,
          review your configuration, and click <i>Create</i>.
        </p>
      </li>
    </ol>

  </div>
</div>
